import { Fragment, useCallback, useMemo } from "react";
import { Box, Button, Icon, Menu, MenuPopup, MenuTrigger, Text, useCloseMenu } from "@salestrak/ui";
import { DataTableCellTrigger, DataTableCellContent } from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { useWorkflowPool } from "#app/components/entity-selectors/hooks";
import { DROPDOWN_MENU_PARTS, type MenuParts } from "#app/components/menus/menu-parts";
import { useWorkspaceData, type WorkspaceStage } from "#app/lib/data/workspace-data-provider";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import { compareStages } from "#app/lib/stage-order";

/** A workflow's stages in category order, run by category — Pending, In
 *  progress, Done, Canceled — each run under its label. Stages with no
 *  category trail as one unlabelled run. */
function stageRuns(stages: readonly WorkspaceStage[]): { label: string | null; stages: WorkspaceStage[] }[] {
  const runs: { label: string | null; stages: WorkspaceStage[] }[] = [];
  for (const stage of [...stages].sort(compareStages)) {
    const label = stageTypeLabel(stage.type);
    const last = runs[runs.length - 1];
    if (last && last.label === label) last.stages.push(stage);
    else runs.push({ label, stages: [stage] });
  }
  return runs;
}

/**
 * THE move-to-workflow rows: one submenu row per OTHER workflow the viewer
 * can see AND edit into (the server lists what is visible; `editableOnly`
 * drops the rest, so a move is never offered only to be refused), each
 * opening that workflow's stages beside it. Picking a stage moves the
 * project straight there — never blind onto the target's default. Composed
 * from the host's menu parts, so the row menu's "Move to Workflow…" submenu
 * and the Workflow cell's menu list the same rows.
 */
export function MoveWorkflowRows({
  workflowId,
  onMove,
  parts = DROPDOWN_MENU_PARTS,
}: {
  /** The project's current workflow — excluded from the rows. */
  workflowId: string;
  onMove: (workflowId: string, stageId: string) => void;
  parts?: MenuParts;
}) {
  const { workflowMap, stagesByWorkflow } = useWorkspaceData();
  const exclude = useMemo(() => [workflowId], [workflowId]);
  const { pool } = useWorkflowPool({ exclude, editableOnly: true });
  const { Item, Sub, SubTrigger, SubPopup } = parts;

  if (pool.items.length === 0) {
    return (
      <Item disabled>
        <Text size="sm" variant="muted">
          No other workflows
        </Text>
      </Item>
    );
  }

  return (
    <>
      {pool.items.map((item) => {
        const workflow = workflowMap.get(item.id);
        if (!workflow) return null;
        const runs = stageRuns(stagesByWorkflow.get(item.id) ?? []);
        return (
          <Sub key={item.id}>
            <SubTrigger disabled={runs.length === 0} disabledReason="This workflow has no stages">
              <EntityTag
                entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
                interactive={false}
                size="sm"
              />
            </SubTrigger>
            <SubPopup width="panel">
              <StageRuns
                workflowId={item.id}
                runs={runs}
                parts={parts}
                onPick={(stageId) => onMove(item.id, stageId)}
              />
            </SubPopup>
          </Sub>
        );
      })}
    </>
  );
}

/**
 * A workflow's stages as menu rows, the way the stage popover lists them:
 * one run per category under its label, a hairline between runs, the stage
 * glyph and name on each row. The FIRST run's label carries the workflow's
 * stage settings as a trailing "…" (the popover's "Edit stages" footer) —
 * whichever category leads, so the door is always on the first label.
 */
function StageRuns({
  workflowId,
  runs,
  parts,
  onPick,
}: {
  workflowId: string;
  runs: { label: string | null; stages: WorkspaceStage[] }[];
  parts: MenuParts;
  onPick: (stageId: string) => void;
}) {
  const { Item, Group, GroupLabel, Separator } = parts;
  const canEditStages = usePermission("manage.edit_workflows");
  const { openStagesDialog } = useStagesDialogActions();
  const closeMenu = useCloseMenu();
  const openSettings = useCallback(() => {
    closeMenu();
    openStagesDialog(workflowId);
  }, [closeMenu, openStagesDialog, workflowId]);

  return (
    <>
      {runs.map((run, index) => (
        <Fragment key={run.label ?? `run-${index}`}>
          {index > 0 ? <Separator /> : null}
          <Group>
            {run.label ? (
              <GroupLabel>
                {/* One rung for every label, with or without the "…": the
                    button's 24px, so the rows never step. */}
                <Box direction="row" align="center" justify="between" gap="sm" height={24}>
                  <Text size="xs" weight="medium" variant="muted">
                    {run.label}
                  </Text>
                  {index === 0 && canEditStages ? (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Stage settings"
                      onClick={openSettings}
                    >
                      <Icon name="ellipsis" size="sm" color="muted" />
                    </Button>
                  ) : null}
                </Box>
              </GroupLabel>
            ) : null}
            {run.stages.map((stage) => (
              <Item key={stage.stageId} onClick={() => onPick(stage.stageId)}>
                <EntityTag
                  entity={{ kind: "stage", id: stage.stageId, stage, workflowId }}
                  interactive={false}
                  size="sm"
                />
              </Item>
            ))}
          </Group>
        </Fragment>
      ))}
    </>
  );
}

/** Whether the rows have anywhere to move to — gates a menu's own row. */
export function useHasMoveTargets(workflowId: string): boolean {
  const exclude = useMemo(() => [workflowId], [workflowId]);
  const { pool } = useWorkflowPool({ exclude, editableOnly: true });
  return pool.items.length > 0;
}

/**
 * The Workflow column's editable cell: the whole cell a menu of the move
 * rows above, wearing the editable-cell ring like every other cell a press
 * edits. A menu, not a popover, so the stage list opens BESIDE its workflow
 * row as a submenu — the trigger stays inside the menu's own tree, which is
 * what a submenu needs to open without closing its parent.
 */
export function WorkflowMenuCell({
  workflowId,
  onMove,
  children,
}: {
  workflowId: string;
  onMove: (workflowId: string, stageId: string) => void;
  children: React.ReactNode;
}) {
  return (
    <Menu>
      <MenuTrigger render={<DataTableCellTrigger aria-haspopup="menu" />}>
        <DataTableCellContent>{children}</DataTableCellContent>
      </MenuTrigger>
      <MenuPopup align="start" width="panel">
        <MoveWorkflowRows workflowId={workflowId} onMove={onMove} />
      </MenuPopup>
    </Menu>
  );
}
